import React from 'react';
import { motion } from 'framer-motion';
import { DatabaseIcon, LayoutTemplateIcon, RefreshCwIcon, TrendingDownIcon } from 'lucide-react';
export function Slide9AnnualCycle() {
  const steps = [{
    phase: 'The Seed',
    title: 'Congress Activation',
    desc: 'Capture baseline HCP knowledge gaps, topic interests, and channel preferences.',
    icon: DatabaseIcon,
    color: 'text-[#E60012]',
    bg: 'bg-[#E60012]/10',
    timing: 'Month 0'
  }, {
    phase: 'The Foundation',
    title: 'Master Assets',
    desc: 'Build a reusable content franchise (Master Teaser + Trailer) tuned to those insights.',
    icon: LayoutTemplateIcon,
    color: 'text-[#6B46C1]',
    bg: 'bg-[#6B46C1]/10',
    timing: 'Month 1'
  }, {
    phase: 'The Engine',
    title: 'Always-On Cadence',
    desc: 'Deploy monthly iterations across WhatsApp, Email and Reps — each tuned by the last.',
    icon: RefreshCwIcon,
    color: 'text-blue-600',
    bg: 'bg-blue-100',
    timing: 'Months 2–12'
  }];
  const tiers = [{
    label: 'Single Pilot',
    commitment: 'One-off campaign',
    rate: '€5,000',
    discount: 'List rate',
    barH: 'h-full',
    featured: false
  }, {
    label: '6-Month Program',
    commitment: '3 campaigns',
    rate: '€4,250',
    discount: '−15% / campaign',
    barH: 'h-[70%]',
    featured: false
  }, {
    label: '12-Month Program',
    commitment: '6+ campaigns',
    rate: '€3,500',
    discount: '−30% / campaign',
    barH: 'h-[48%]',
    featured: true
  }];
  return <div className="h-full w-full bg-[#F8F9FA] text-[#2D3748] flex flex-col p-12 md:px-24 md:pt-14 md:pb-16 relative overflow-hidden" data-id="element-306">
      <header className="mb-8 text-center max-w-4xl mx-auto" data-id="element-307">
        <motion.h2 className="text-4xl md:text-5xl font-bold text-[#1A202C] mb-3" initial={{
        opacity: 0,
        y: -20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6
      }} data-id="element-308">
          Beyond the Congress:{' '}
          <span className="text-[#E60012]" data-id="element-309">The Annual Cycle</span>
        </motion.h2>
        <motion.p className="text-lg text-[#4A5568] font-light" initial={{
        opacity: 0,
        y: -20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6,
        delay: 0.1
      }} data-id="element-310">
          Turn a 3-day event into 365 days of engagement — and the more you
          commit, the lower the cost per campaign.
        </motion.p>
      </header>

      <main className="flex-1 flex flex-col gap-8 max-w-6xl mx-auto w-full min-h-0" data-id="element-311">
        {/* The Cycle */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 relative flex-[1.1] min-h-0" data-id="element-312">
          {/* Connecting Line */}
          <div className="hidden md:block absolute top-1/2 left-0 w-full h-1 bg-gray-200 -translate-y-1/2 z-0" data-id="element-313" />

          {steps.map((step, index) => {
          const Icon = step.icon;
          return <motion.div key={step.title} className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100 relative z-10 flex flex-col justify-center hover:shadow-md transition-shadow" initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            delay: 0.2 + index * 0.12
          }} data-id="element-314">
                <div className="flex justify-between items-start mb-6" data-id="element-315">
                  <div className={`p-4 rounded-xl ${step.bg}`} data-id="element-316">
                    <Icon className={`w-8 h-8 ${step.color}`} data-id="element-317" />
                  </div>
                  <span className="bg-gray-50 text-gray-600 text-[11px] font-bold px-3 py-1.5 rounded-full uppercase tracking-wider border border-gray-100" data-id="element-318">
                    {step.timing}
                  </span>
                </div>
                <div className="mb-2 text-xs font-bold uppercase tracking-wider text-gray-400" data-id="element-319">
                  {step.phase}
                </div>
                <h3 className="text-2xl font-bold text-[#1A202C] mb-3 leading-tight" data-id="element-320">
                  {step.title}
                </h3>
                <p className="text-base text-[#4A5568] leading-relaxed" data-id="element-321">
                  {step.desc}
                </p>
              </motion.div>;
        })}
        </div>

        {/* Cost decline with commitment */}
        <motion.div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 md:p-10 flex flex-col md:flex-row items-center gap-10 flex-1 min-h-0" initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6,
        delay: 0.6
      }} data-id="element-322">
          <div className="md:w-1/4 shrink-0" data-id="element-323">
            <div className="flex items-center gap-2 mb-3" data-id="element-324">
              <TrendingDownIcon className="w-5 h-5 text-[#E60012]" data-id="element-325" />
              <span className="text-xs font-bold uppercase tracking-wider text-gray-400" data-id="element-326">
                Cost per campaign
              </span>
            </div>
            <h3 className="text-2xl md:text-3xl font-bold text-[#1A202C] leading-tight" data-id="element-327">
              Commit longer,{' '}
              <span className="text-[#E60012]" data-id="element-328">pay less each cycle.</span>
            </h3>
          </div>

          <div className="flex-1 grid grid-cols-3 gap-6 md:gap-10 items-end h-full w-full py-2" data-id="element-329">
            {tiers.map((tier, i) => <div key={tier.label} className="h-full flex flex-col justify-end items-center" data-id="element-330">
                <div className="text-center mb-3" data-id="element-331">
                  <p className={`font-bold text-2xl md:text-3xl leading-none ${tier.featured ? 'text-[#E60012]' : 'text-[#1A202C]'}`} data-id="element-332">
                    {tier.rate}
                  </p>
                  <span className={`text-xs font-bold ${tier.featured ? 'text-[#E60012]' : 'text-gray-400'}`} data-id="element-333">
                    {tier.discount}
                  </span>
                </div>
                <motion.div className={`w-full rounded-t-xl ${tier.barH} ${tier.featured ? 'bg-[#E60012]' : i === 0 ? 'bg-gray-300' : 'bg-gray-400'} flex items-start justify-center pt-2 relative`} initial={{
              scaleY: 0
            }} animate={{
              scaleY: 1
            }} transition={{
              delay: 0.8 + i * 0.15,
              duration: 0.5,
              ease: [0.4, 0, 0.2, 1]
            }} style={{
              transformOrigin: 'bottom'
            }} data-id="element-334" />
                <div className="text-center mt-3" data-id="element-335">
                  <p className="text-sm font-bold text-[#1A202C] leading-tight" data-id="element-336">
                    {tier.label}
                  </p>
                  <p className="text-xs text-gray-400" data-id="element-337">{tier.commitment}</p>
                </div>
              </div>)}
          </div>
        </motion.div>
      </main>

      <motion.footer className="mt-6 text-center" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} transition={{
      duration: 0.6,
      delay: 1.1
    }} data-id="element-338">
        <p className="text-sm font-medium text-gray-500" data-id="element-339">
          Indicative rates anchored to a ~€5,000 per-campaign list price — final
          break points negotiable on program length.
        </p>
      </motion.footer>
    </div>;
}